import { useEffect, useRef } from "react";

/**
 * On iOS Safari, a `position: fixed` ancestor (`.sticky-edit-card`,
 * ProfileCard/SubscriptionCard's mobile edit mode -- see StickyEditCard.scss)
 * can stop responding to touch scrolling for a few seconds after the
 * on-screen keyboard closes. This is a known WebKit quirk: the browser
 * doesn't recompute hit-testing for the fixed ancestor's scrollable body
 * until something else forces a reflow. Toggling `overflow` on the body once
 * the visual viewport resizes back (keyboard closing) forces that recompute
 * immediately instead of waiting for the browser to get to it on its own.
 */
export const useFixStickyCardScrollAfterKeyboardClose = (enabled: boolean) => {
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!enabled || !viewport) {
      return;
    }

    const handleResize = () => {
      const body = bodyRef.current;
      if (!body) {
        return;
      }
      body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        body.style.overflow = "";
      });
    };

    viewport.addEventListener("resize", handleResize);

    return () => viewport.removeEventListener("resize", handleResize);
  }, [enabled]);

  return bodyRef;
};
